/**
 * Cockpit-facing AI usage helpers: map ledger / outage rows onto the shared
 * meter contract without inventing figures the rows did not carry.
 */
import {
  AI_USAGE_GUIDANCE,
  adviseAiUsage,
  buildAiCapacityMeters,
  checkAiUsagePolicy,
  DEFAULT_AI_USAGE_POLICY,
  measurementBadge,
  quotaSignalFromStatedFigures,
  tagMeasurement,
  type AiCapacityMeter,
  type AiUsagePolicy,
  type UsageAdvisory,
  type UsageLedgerSnapshot,
  type UsageQuotaSignal,
  type UsageReadingOrigin,
  type UsageConfidence,
  type UsageMeasureUnit,
  type UsageHosting,
  type UsageBillingKind,
} from '@brunel/shared';
import type {
  AiConnectionStatusRow,
  AiLocalRuntimeStatusRow,
  AiUsagePolicyRow,
  ModelOfferingRef,
  NodeCapacityRow,
  NodeOffering,
  NodePresence,
  NodePressureRow,
  ProviderUsageLatest,
} from './data';
import { AI_CONNECTIONS_DISPLAY } from './ai-connections-catalog';
import type { ExternalAiBridgeStatus } from './connector-center-view';

export type { AiUsagePolicyRow, ProviderUsageLatest };

export {
  AI_USAGE_GUIDANCE,
  adviseAiUsage,
  buildAiCapacityMeters,
  checkAiUsagePolicy,
  DEFAULT_AI_USAGE_POLICY,
  measurementBadge,
  tagMeasurement,
};
export type { AiCapacityMeter, AiUsagePolicy, UsageAdvisory, UsageLedgerSnapshot };

export type LocalRuntimeMeterState =
  | 'available' | 'busy' | 'memory_pressure' | 'offline' | 'unavailable' | 'unknown';

export interface LocalRuntimeCapacityMeter extends AiCapacityMeter {
  readonly meterKind: 'local_runtime';
  readonly localRuntime: {
    readonly nodeName: string;
    readonly nodeStatus: string;
    readonly installed: boolean | null;
    readonly serviceState: 'reachable' | 'unreachable' | 'unknown';
    readonly runtimeAvailable: boolean | null;
    readonly runtimeVersion: string | null;
    readonly installedModelsKnown: boolean;
    readonly installedModels: AiLocalRuntimeStatusRow['installed_models'];
    readonly loadedModelsKnown: boolean;
    readonly loadedModels: AiLocalRuntimeStatusRow['loaded_models'];
    readonly offeringModels: readonly string[];
    readonly state: LocalRuntimeMeterState;
    readonly lastProbeAt: string | null;
    readonly lastSuccessfulProbeAt: string | null;
    readonly cpuLoadPercent: number | null;
    readonly graphicsUtilPercent: number | null;
    readonly memoryFreePercent: number | null;
    readonly videoMemoryFreePercent: number | null;
    readonly accepting: boolean | null;
    readonly holdingBack: string | null;
  };
}

export function isLocalRuntimeMeter(meter: AiCapacityMeter): meter is LocalRuntimeCapacityMeter {
  return (meter as Partial<LocalRuntimeCapacityMeter>).meterKind === 'local_runtime';
}

export type ExternalAiEntityKey =
  | 'openai-chatgpt-mcp'
  | 'xai-grok-mcp'
  | 'xai-grok-build'
  | 'grok-bot';

export type ExternalAiEntityKind = 'external_mcp_bridge' | 'executable_runtime' | 'external_teammate';
export type ExternalAiConnectionState = 'connected' | 'available' | 'unavailable' | 'unknown';

export interface ExternalAiStatusMeter extends AiCapacityMeter {
  readonly meterKind: 'external_ai_status';
  readonly externalAi: {
    readonly entityKey: ExternalAiEntityKey;
    readonly entityKind: ExternalAiEntityKind;
    readonly effectiveCapability: 'read_only' | 'write_capable' | 'unavailable' | 'unknown';
    readonly connectionState: ExternalAiConnectionState;
    readonly installed: boolean | null;
    readonly authState: 'signed_in' | 'signed_out' | 'unknown';
    readonly lastVerifiedAt: string | null;
    readonly evidenceSource: string;
    readonly note: string;
  };
}

export function isExternalAiStatusMeter(meter: AiCapacityMeter): meter is ExternalAiStatusMeter {
  return (meter as Partial<ExternalAiStatusMeter>).meterKind === 'external_ai_status';
}

function externalTone(state: ExternalAiConnectionState): AiCapacityMeter['tone'] {
  if (state === 'connected' || state === 'available') return 'success';
  if (state === 'unavailable') return 'danger';
  return 'neutral';
}

function latestConnection(
  rows: readonly AiConnectionStatusRow[],
  connectionKey: string,
): AiConnectionStatusRow | null {
  return [...rows]
    .filter((row) => row.connection_key === connectionKey)
    .sort((a, b) => b.last_probe_at.localeCompare(a.last_probe_at))[0] ?? null;
}

function externalBridgeMeter(
  surface: 'chatgpt' | 'grok',
  statuses: readonly ExternalAiBridgeStatus[],
): ExternalAiStatusMeter {
  const live = statuses.find((row) => row.surface === surface) ?? null;
  const connected = live?.connected ?? null;
  const state: ExternalAiConnectionState = connected === true ? 'connected' : connected === false ? 'unavailable' : 'unknown';
  const capability = connected === false ? 'unavailable' : connected === true ? live?.capability ?? 'unknown' : 'unknown';
  const chatgpt = surface === 'chatgpt';
  const title = chatgpt ? 'ChatGPT MCP' : 'Grok MCP';
  const entityKey: ExternalAiEntityKey = chatgpt ? 'openai-chatgpt-mcp' : 'xai-grok-mcp';
  const provider = chatgpt ? 'openai' : 'xai';
  const source = live?.source ?? 'MCP Phase 1 contract · no live client negotiation recorded';
  const capabilityLabel = capability === 'read_only'
    ? 'Read-only'
    : capability === 'write_capable'
      ? 'Write-capable'
      : capability === 'unavailable'
        ? 'Unavailable'
        : 'Capability Unknown';
  return {
    meterKind: 'external_ai_status',
    key: 'external-ai:' + entityKey,
    title,
    provider,
    runtime: 'mcp-gateway',
    model: null,
    nodeId: null,
    poolKey: null,
    usedPercent: null,
    remainingLabel: null,
    usedLabel: null,
    resetsLabel: null,
    measurementLabel: 'Status',
    measurementPhrase: source,
    quotaSignal: 'unknown',
    band: 'unknown',
    tone: externalTone(state),
    hosting: 'cloud',
    billingKind: 'subscription',
    detailLine: title + ' · capacity Unknown · ' + capabilityLabel,
    known: live !== null,
    externalAi: {
      entityKey,
      entityKind: 'external_mcp_bridge',
      effectiveCapability: capability,
      connectionState: state,
      installed: null,
      authState: 'unknown',
      lastVerifiedAt: live?.lastCheckedAt ?? null,
      evidenceSource: source,
      note: chatgpt
        ? 'Regular ChatGPT is separate from Codex. Capacity stays Unknown unless a supported ChatGPT allowance source reports it.'
        : 'Grok MCP is separate from Grok Build and Grok Bot. Capacity stays Unknown unless a supported Grok chat allowance source reports it.',
    },
  };
}

function grokBotMeter(connections: readonly AiConnectionStatusRow[]): ExternalAiStatusMeter {
  const status = latestConnection(connections, 'grok-bot');
  const state: ExternalAiConnectionState = status?.installed === true
    ? 'available'
    : status?.installed === false
      ? 'unavailable'
      : 'unknown';
  return {
    meterKind: 'external_ai_status',
    key: 'external-ai:grok-bot',
    title: 'Grok Bot',
    provider: 'xai',
    runtime: null,
    model: null,
    nodeId: status?.node_id ?? null,
    poolKey: 'grok-bot',
    usedPercent: null,
    remainingLabel: null,
    usedLabel: null,
    resetsLabel: null,
    measurementLabel: 'Status',
    measurementPhrase: status ? 'AI connection probe' : 'No Grok Bot probe recorded',
    quotaSignal: 'unknown',
    band: 'unknown',
    tone: externalTone(state),
    hosting: 'cloud',
    billingKind: 'subscription',
    detailLine: 'Grok Bot · external teammate · usage Unknown',
    known: status !== null,
    externalAi: {
      entityKey: 'grok-bot',
      entityKind: 'external_teammate',
      effectiveCapability: 'unknown',
      connectionState: state,
      installed: status?.installed ?? null,
      authState: status?.auth_state ?? 'unknown',
      lastVerifiedAt: status?.last_probe_at ?? null,
      evidenceSource: status ? 'AI connection probe' : 'No Grok Bot probe recorded',
      note: 'Weekly usage exists in the provider product, but no supported machine-readable usage source is available to Brunel.',
    },
  };
}

function grokBuildMeter(
  quota: AiCapacityMeter | null,
  connections: readonly AiConnectionStatusRow[],
  usageObservedAt: string | null,
): ExternalAiStatusMeter {
  const status = latestConnection(connections, 'xai-grok-build');
  const state: ExternalAiConnectionState = status?.installed === false || status?.auth_state === 'signed_out'
    ? 'unavailable'
    : status?.installed === true && status.auth_state === 'signed_in'
      ? 'available'
      : 'unknown';
  const knownCapacity = quota?.usedPercent !== null && quota?.usedPercent !== undefined;
  return {
    meterKind: 'external_ai_status',
    key: 'external-ai:xai-grok-build',
    title: 'Grok Build',
    provider: 'xai',
    runtime: 'grok-build',
    model: quota?.model ?? null,
    nodeId: status?.node_id ?? quota?.nodeId ?? null,
    poolKey: 'grok-weekly',
    usedPercent: quota?.usedPercent ?? null,
    remainingLabel: quota?.remainingLabel ?? null,
    usedLabel: quota?.usedLabel ?? null,
    resetsLabel: quota?.resetsLabel ?? null,
    measurementLabel: quota?.measurementLabel ?? 'Status',
    measurementPhrase: quota?.measurementPhrase ?? (status ? 'AI connection probe' : 'No Grok Build probe recorded'),
    quotaSignal: quota?.quotaSignal ?? (status?.auth_state === 'signed_out' ? 'signed_out' : 'unknown'),
    band: quota?.band ?? 'unknown',
    tone: quota?.tone ?? externalTone(state),
    hosting: quota?.hosting ?? 'cloud',
    billingKind: quota?.billingKind ?? 'subscription',
    detailLine: knownCapacity
      ? String(Math.round((quota?.usedPercent ?? 0) * 10) / 10) + '% used · Grok Build'
      : 'Grok Build · capacity Unknown · ' + (state === 'available' ? 'runtime signed in' : state),
    known: quota?.known === true || status !== null,
    externalAi: {
      entityKey: 'xai-grok-build',
      entityKind: 'executable_runtime',
      effectiveCapability: 'unknown',
      connectionState: state,
      installed: status?.installed ?? null,
      authState: status?.auth_state ?? 'unknown',
      lastVerifiedAt: usageObservedAt ?? status?.last_probe_at ?? null,
      evidenceSource: quota !== null ? quota.measurementPhrase : status ? 'AI connection probe' : 'No Grok Build evidence recorded',
      note: 'Executable Grok Build runtime. Its /usage pool is separate from Grok Bot and does not prove a Grok MCP bridge.',
    },
  };
}

function buildExternalAiMeters(input: {
  readonly quotaMeters: readonly AiCapacityMeter[];
  readonly usage: readonly ProviderUsageLatest[];
  readonly connections: readonly AiConnectionStatusRow[];
  readonly bridges: readonly ExternalAiBridgeStatus[];
}): { readonly meters: readonly ExternalAiStatusMeter[]; readonly consumedQuotaKeys: ReadonlySet<string> } {
  const latestGrokBuildUsage = [...input.usage]
    .filter((row) => row.provider_key === 'xai' && row.pool_key === 'grok-weekly')
    .sort((a, b) => b.observed_at.localeCompare(a.observed_at))[0] ?? null;
  const grokBuildQuotaKey = latestGrokBuildUsage === null ? null : [
    latestGrokBuildUsage.provider_key,
    latestGrokBuildUsage.account_ref ?? '',
    latestGrokBuildUsage.pool_key ?? '',
    latestGrokBuildUsage.runtime_key ?? '',
  ].join(':');
  const grokBuildQuota = grokBuildQuotaKey === null
    ? null
    : input.quotaMeters.find((meter) => meter.key === grokBuildQuotaKey) ?? null;
  const consumed = new Set<string>();
  for (const meter of input.quotaMeters) {
    if (meter.provider === 'xai' && meter.poolKey === 'grok-weekly') consumed.add(meter.key);
  }
  return {
    meters: [
      externalBridgeMeter('chatgpt', input.bridges),
      externalBridgeMeter('grok', input.bridges),
      grokBuildMeter(grokBuildQuota, input.connections, latestGrokBuildUsage?.observed_at ?? null),
      grokBotMeter(input.connections),
    ],
    consumedQuotaKeys: consumed,
  };
}

const ORIGINS = new Set<UsageReadingOrigin>([
  'provider_api', 'cli_output', 'local_runtime', 'account_export', 'owner_entered', 'unknown', 'derived',
]);
const CONFIDENCES = new Set<UsageConfidence>(['exact', 'coarse', 'unknown']);
const UNITS = new Set<UsageMeasureUnit>(['percent', 'tokens', 'credits', 'requests', 'unknown']);

function asOrigin(value: string): UsageReadingOrigin {
  return ORIGINS.has(value as UsageReadingOrigin) ? value as UsageReadingOrigin : 'unknown';
}
function asConfidence(value: string): UsageConfidence {
  return CONFIDENCES.has(value as UsageConfidence) ? value as UsageConfidence : 'unknown';
}
function asUnit(value: string): UsageMeasureUnit {
  return UNITS.has(value as UsageMeasureUnit) ? value as UsageMeasureUnit : 'unknown';
}

function hostingFor(origin: UsageReadingOrigin, runtime: string | null): UsageHosting {
  if (origin === 'local_runtime') return 'local';
  if (runtime !== null && /ollama|local/i.test(runtime)) return 'local';
  if (origin === 'unknown') return 'unknown';
  return 'cloud';
}

function billingFor(unit: UsageMeasureUnit, origin: UsageReadingOrigin): UsageBillingKind {
  if (origin === 'local_runtime') return 'unknown';
  if (unit === 'percent') return 'subscription';
  if (unit === 'tokens' || unit === 'credits' || unit === 'requests') return 'api_metered';
  return 'unknown';
}

/**
 * Quota signal from a ledger row alone.
 * Missing figures → unknown (never silently ok). Exhaustion/near-limit only
 * when remaining/used/limit legitimately imply them.
 */
export function quotaSignalFromProviderUsage(row: ProviderUsageLatest): UsageQuotaSignal {
  return quotaSignalFromStatedFigures({
    usedValue: row.used_value,
    remainingValue: row.remaining_value,
    limitValue: row.limit_value,
    measureUnit: asUnit(row.measure_unit),
    resetsAt: row.resets_at,
  });
}

/** Map a provider_usage_latest row onto the suite ledger snapshot. */
export function snapshotFromProviderUsage(row: ProviderUsageLatest): UsageLedgerSnapshot {
  const origin = asOrigin(row.source);
  const unit = asUnit(row.measure_unit);
  const runtime = row.runtime_key;
  return {
    provider: row.provider_key,
    accountRef: row.account_ref,
    poolKey: row.pool_key,
    runtime,
    model: row.model_key,
    nodeId: row.node_id,
    measureUnit: unit,
    usedValue: row.used_value,
    remainingValue: row.remaining_value,
    limitValue: row.limit_value,
    resetsAt: row.resets_at,
    confidence: asConfidence(row.confidence),
    readingOrigin: origin,
    observedAt: row.observed_at,
    quotaSignal: quotaSignalFromProviderUsage(row),
    hosting: hostingFor(origin, runtime),
    billingKind: billingFor(unit, origin),
    label: row.provider_key,
  };
}

export function policyFromRow(row: AiUsagePolicyRow | null | undefined): AiUsagePolicy {
  if (!row) return DEFAULT_AI_USAGE_POLICY;
  const behavior = row.exhaustion_behavior;
  return {
    softLimitPercent: row.soft_limit_percent,
    hardLimitPercent: row.hard_limit_percent,
    reservePercent: row.reserve_percent,
    warnThresholds: row.warn_thresholds && row.warn_thresholds.length > 0
      ? [...row.warn_thresholds]
      : [...DEFAULT_AI_USAGE_POLICY.warnThresholds],
    exhaustionBehavior:
      behavior === 'auto_route' || behavior === 'pause_lane' || behavior === 'require_approval' || behavior === 'advise_only'
        ? behavior
        : 'advise_only',
    neverSubstitutePinned: row.never_substitute_pinned,
  };
}

function resourceState(input: {
  readonly node: NodePresence;
  readonly local: AiLocalRuntimeStatusRow | null;
  readonly capacity: NodeCapacityRow | null;
}): LocalRuntimeMeterState {
  if (input.node.status === 'offline') return 'offline';
  if (input.capacity?.holding_back && /memory|video|graphics/i.test(input.capacity.holding_back)) {
    return 'memory_pressure';
  }
  if (input.local?.service_state === 'unreachable') return 'unavailable';
  if (input.local?.service_state === 'reachable') {
    if (input.local.loaded_models_known && input.local.loaded_models.length > 0) return 'busy';
    return 'available';
  }
  return 'unknown';
}

function localTone(state: LocalRuntimeMeterState): AiCapacityMeter['tone'] {
  if (state === 'offline' || state === 'unavailable') return 'danger';
  if (state === 'memory_pressure' || state === 'busy') return 'attention';
  if (state === 'available') return 'success';
  return 'neutral';
}

function buildLocalRuntimeMeters(input: {
  readonly connections: readonly AiConnectionStatusRow[];
  readonly localStatuses: readonly AiLocalRuntimeStatusRow[];
  readonly nodes: readonly NodePresence[];
  readonly pressure: readonly NodePressureRow[];
  readonly capacity: readonly NodeCapacityRow[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
}): readonly LocalRuntimeCapacityMeter[] {
  const display = AI_CONNECTIONS_DISPLAY.find((row) => row.meterMode === 'local_unlimited') ?? null;
  const runtimeKey = display?.runtimeKeys[0] ?? null;
  if (display === null || runtimeKey === null) return [];

  const offeringByKey = new Map(
    input.modelOfferings
      .filter((row) => row.execution_location === 'local' && row.runtime_key === runtimeKey)
      .map((row) => [row.key, row] as const),
  );
  const nodeIds = new Set<string>();
  for (const row of input.connections) if (row.connection_key === display.connectionKey) nodeIds.add(row.node_id);
  for (const row of input.localStatuses) if (row.connection_key === display.connectionKey) nodeIds.add(row.node_id);
  for (const row of input.nodeOfferings) if (offeringByKey.has(row.offering_key)) nodeIds.add(row.node_id);

  const meters: LocalRuntimeCapacityMeter[] = [];
  for (const nodeId of nodeIds) {
    const node = input.nodes.find((row) => row.node_id === nodeId);
    if (!node) continue;
    const connection = input.connections.find(
      (row) => row.node_id === nodeId && row.connection_key === display.connectionKey,
    ) ?? null;
    const local = input.localStatuses.find(
      (row) => row.node_id === nodeId && row.connection_key === display.connectionKey,
    ) ?? null;
    const pressure = input.pressure.find((row) => row.node_id === nodeId) ?? null;
    const capacity = input.capacity.find((row) => row.node_id === nodeId) ?? null;
    const offeringModels = [...new Set(input.nodeOfferings
      .filter((row) => row.node_id === nodeId)
      .map((row) => offeringByKey.get(row.offering_key)?.model_key ?? null)
      .filter((model): model is string => model !== null))].sort();
    const state = resourceState({ node, local, capacity });
    const loadedModel = local?.loaded_models_known ? local.loaded_models[0]?.name ?? null : null;
    const memoryBits = [
      pressure?.memory_free_percent === null || pressure?.memory_free_percent === undefined
        ? null : `${Math.round(pressure.memory_free_percent * 10) / 10}% memory free`,
      pressure?.video_memory_free_percent === null || pressure?.video_memory_free_percent === undefined
        ? null : `${Math.round(pressure.video_memory_free_percent * 10) / 10}% video memory free`,
    ].filter((value): value is string => value !== null);

    meters.push({
      meterKind: 'local_runtime',
      key: `local:${display.connectionKey}:${nodeId}`,
      title: display.meterLabel,
      provider: display.providerKey,
      runtime: runtimeKey,
      model: loadedModel,
      nodeId,
      poolKey: display.poolKey,
      usedPercent: null,
      remainingLabel: null,
      usedLabel: null,
      resetsLabel: null,
      measurementLabel: 'Local',
      measurementPhrase: 'node-local runtime status; no account quota applies',
      quotaSignal: 'unknown',
      band: 'unknown',
      tone: localTone(state),
      hosting: 'local',
      billingKind: 'unknown',
      detailLine: [
        'Local / Unlimited',
        node.name,
        state.replaceAll('_', ' '),
        ...memoryBits,
      ].join(' · '),
      known: connection !== null || local !== null || offeringModels.length > 0,
      localRuntime: {
        nodeName: node.name,
        nodeStatus: node.status,
        installed: connection?.installed ?? (offeringModels.length > 0 ? true : null),
        serviceState: local?.service_state ?? 'unknown',
        runtimeAvailable: offeringModels.length > 0 ? true : connection?.installed === false ? false : null,
        runtimeVersion: local?.runtime_version ?? null,
        installedModelsKnown: local?.installed_models_known ?? false,
        installedModels: local?.installed_models ?? [],
        loadedModelsKnown: local?.loaded_models_known ?? false,
        loadedModels: local?.loaded_models ?? [],
        offeringModels,
        state,
        lastProbeAt: connection?.last_probe_at ?? null,
        lastSuccessfulProbeAt: local?.last_successful_probe_at ?? null,
        cpuLoadPercent: pressure?.cpu_load_percent ?? null,
        graphicsUtilPercent: pressure?.graphics_util_percent ?? null,
        memoryFreePercent: pressure?.memory_free_percent ?? null,
        videoMemoryFreePercent: pressure?.video_memory_free_percent ?? null,
        accepting: capacity?.accepting ?? null,
        holdingBack: capacity?.holding_back ?? null,
      },
    });
  }
  return meters.sort((left, right) => left.localRuntime.nodeName.localeCompare(right.localRuntime.nodeName));
}

export function cockpitAiMeters(
  usage: readonly ProviderUsageLatest[],
  outages: readonly { runtime_key: string; reason_code: string; available_from: string | null; name?: string | null }[],
  policyRow: AiUsagePolicyRow | null | undefined,
  nowMs: number,
  localSources?: {
    readonly connections: readonly AiConnectionStatusRow[];
    readonly localStatuses: readonly AiLocalRuntimeStatusRow[];
    readonly nodes: readonly NodePresence[];
    readonly pressure: readonly NodePressureRow[];
    readonly capacity: readonly NodeCapacityRow[];
    readonly nodeOfferings: readonly NodeOffering[];
    readonly modelOfferings: readonly ModelOfferingRef[];
    readonly externalBridges?: readonly ExternalAiBridgeStatus[];
  },
): readonly AiCapacityMeter[] {
  const quotaMeters = buildAiCapacityMeters(
    usage.map(snapshotFromProviderUsage),
    outages,
    nowMs,
    policyFromRow(policyRow),
  );
  const localMeters = localSources ? buildLocalRuntimeMeters(localSources) : [];
  if (!localSources) return [...quotaMeters, ...localMeters].sort((left, right) => left.title.localeCompare(right.title));
  const external = buildExternalAiMeters({
    quotaMeters,
    usage,
    connections: localSources.connections,
    bridges: localSources.externalBridges ?? [],
  });
  const remainingQuotaMeters = quotaMeters.filter((meter) => !external.consumedQuotaKeys.has(meter.key));
  return [...remainingQuotaMeters, ...localMeters, ...external.meters]
    .sort((left, right) => left.title.localeCompare(right.title));
}

export function cockpitAiAdvisories(
  meters: readonly AiCapacityMeter[],
  policyRow: AiUsagePolicyRow | null | undefined,
): readonly UsageAdvisory[] {
  return adviseAiUsage(
    meters.filter((meter) => !isLocalRuntimeMeter(meter) && !isExternalAiStatusMeter(meter)),
    policyFromRow(policyRow),
  );
}
